import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useBookingFormStore } from '../providers/BookingFormStoreProvider'
import {
  ILang,
  ILanguage,
  IRouteQueryParams,
  IRouteQueryParamsWithDynamicParams,
} from '../store/data'

let pendingQueryMutations = 0
let queryMutationQueue: Promise<void> = Promise.resolve()

interface QueryParams {
  addQueryParameter: (param: string, value: string | null | undefined) => Promise<void>
  removeAllStateQueryParameters: () => Promise<void>
  removeQueryParameter: (param: keyof IRouteQueryParams) => Promise<void>
  removeQueryParametersArray: (params: Array<keyof IRouteQueryParams>) => Promise<void>
  routerQueryParams: IRouteQueryParamsWithDynamicParams
}

export const GUEST_PARAM = 'guest'
export const HOTEL_PARAM = 'hotel'
export const LANG_PARAM = 'lang'
export const ROOM_TYPE_PARAM = 'roomType'
export const VISIT_START_PARAM = 'visitStart'
export const VISIT_END_PARAM = 'visitEnd'
export const VISIT_TYPE_PARAM = 'visitType'
export const AFFIL_USER_NAME = 'affilUserName'
export const DESTINATION_PARAM = 'destination'

export const stateQueryParams = [
  GUEST_PARAM,
  HOTEL_PARAM,
  LANG_PARAM,
  ROOM_TYPE_PARAM,
  VISIT_START_PARAM,
  VISIT_END_PARAM,
  VISIT_TYPE_PARAM,
  AFFIL_USER_NAME,
  DESTINATION_PARAM,
]

const toFlatQueryObject = (searchParams: URLSearchParams): Record<string, string> => {
  const query: Record<string, string> = {}

  searchParams.forEach((value, key) => {
    query[key] = value
  })

  return query
}

export const useQueryParams = (): QueryParams => {
  const router = useRouter()
  const setDeletingQueryParameter = useBookingFormStore((state) => state.setDeletingQueryParameter)

  const startQueryMutation = () => {
    pendingQueryMutations += 1

    if (pendingQueryMutations === 1) {
      setDeletingQueryParameter(true)
    }
  }

  const finishQueryMutation = () => {
    pendingQueryMutations = Math.max(0, pendingQueryMutations - 1)

    if (pendingQueryMutations === 0) {
      setDeletingQueryParameter(false)
    }
  }

  const getCurrentSearchParams = () =>
    new URLSearchParams(
      typeof window === 'undefined' ? router.asPath.split('?')[1] || '' : window.location.search
    )

  const queueQueryMutation = async (mutation: () => Promise<void>) => {
    const queuedMutation = queryMutationQueue.then(mutation, mutation)

    // Keep the shared queue usable even when an individual route change fails.
    queryMutationQueue = queuedMutation.catch(() => {})
    await queuedMutation
  }

  const replaceSearchParams = async (nextSearchParams: URLSearchParams) => {
    const currentSearchParams = getCurrentSearchParams()

    if (currentSearchParams.toString() === nextSearchParams.toString()) {
      return
    }

    const currentPathname =
      typeof window === 'undefined' ? router.pathname : window.location.pathname
    const nextRoute = nextSearchParams.toString()
      ? `${currentPathname}?${nextSearchParams.toString()}`
      : currentPathname

    await router.replace(nextRoute, undefined, { shallow: true })
  }

  const setQueryParameterValueByName = async (param: string, value: string | null | undefined) => {
    const currentSearchParams = getCurrentSearchParams()
    const normalizedNextValue = value ?? null
    const currentValues = currentSearchParams.getAll(param)
    const alreadyHasNextValue =
      normalizedNextValue === null
        ? currentValues.length === 0
        : currentValues.length === 1 && currentValues[0] === normalizedNextValue

    if (alreadyHasNextValue) {
      return
    }

    startQueryMutation()

    try {
      await queueQueryMutation(async () => {
        // Another hook may have changed the URL while this mutation was queued.
        // Re-read it here so cleanup operations merge instead of restoring stale params.
        const latestSearchParams = getCurrentSearchParams()
        const latestValues = latestSearchParams.getAll(param)
        const latestAlreadyHasNextValue =
          normalizedNextValue === null
            ? latestValues.length === 0
            : latestValues.length === 1 && latestValues[0] === normalizedNextValue

        if (latestAlreadyHasNextValue) {
          return
        }

        const nextSearchParams = new URLSearchParams(latestSearchParams)

        if (normalizedNextValue === null) {
          nextSearchParams.delete(param)
        } else {
          // URLSearchParams.set also collapses duplicate values for this key.
          nextSearchParams.set(param, normalizedNextValue)
        }

        await replaceSearchParams(nextSearchParams)
      })
    } finally {
      finishQueryMutation()
    }
  }

  const addQueryParameter = async (param: string, value: string | null | undefined) => {
    await setQueryParameterValueByName(param, value)
  }

  const removeQueryParameter = async (param: keyof IRouteQueryParams) => {
    await setQueryParameterValueByName(param, null)
  }

  const removeQueryParametersArray = async (params: Array<keyof IRouteQueryParams>) => {
    const uniqueParams = Array.from(new Set(params))
    const hasChanges = uniqueParams.some((param) => getCurrentSearchParams().has(param))

    if (!hasChanges) {
      return
    }

    startQueryMutation()

    try {
      await queueQueryMutation(async () => {
        const latestSearchParams = getCurrentSearchParams()
        const nextSearchParams = new URLSearchParams(latestSearchParams)
        let hasLatestChanges = false

        uniqueParams.forEach((param) => {
          if (nextSearchParams.has(param)) {
            nextSearchParams.delete(param)
            hasLatestChanges = true
          }
        })

        if (hasLatestChanges) {
          await replaceSearchParams(nextSearchParams)
        }
      })
    } finally {
      finishQueryMutation()
    }
  }

  const removeAllStateQueryParameters = async () => {
    await removeQueryParametersArray(stateQueryParams as Array<keyof IRouteQueryParams>)
  }

  const routerQueryParams = useMemo(() => {
    const currentSearchParams = new URLSearchParams(router.asPath.split('?')[1] || '')

    return {
      affilUserName: currentSearchParams.get(AFFIL_USER_NAME),
      destination: currentSearchParams.get(DESTINATION_PARAM),
      guest: currentSearchParams.get(GUEST_PARAM),
      hotel: currentSearchParams.get(HOTEL_PARAM),
      lang: currentSearchParams.get(LANG_PARAM) as keyof ILang | ILanguage | null,
      roomType: currentSearchParams.get(ROOM_TYPE_PARAM),
      visitEnd: currentSearchParams.get(VISIT_END_PARAM),
      visitStart: currentSearchParams.get(VISIT_START_PARAM),
      visitType: currentSearchParams.get(VISIT_TYPE_PARAM),
      ...toFlatQueryObject(currentSearchParams),
    } as IRouteQueryParamsWithDynamicParams
  }, [router.asPath])

  return {
    addQueryParameter,
    removeAllStateQueryParameters,
    removeQueryParameter,
    removeQueryParametersArray,
    routerQueryParams,
  }
}
